import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from '@/hooks';
import { buildOrderLinksPayload } from '@/lib/mappers/links';
import {
  linksApi,
  useLinkOrderShipmentsMutation,
  useLinkShipmentOrdersMutation,
} from '@/lib/api/linksApi';
import { EMPTY_LIST } from '@/lib/utils';
import { useLinkableCandidates } from './useLinkableCandidates';
import { LinkOrdersModal, LINK_ORDER_FLOWS, isBulkOrderFlow } from './LinkOrdersModal';

/**
 * Props:
 *   module          route module segment, for the REST save
 *   orders          the orders being linked; one, or a list bulk selection
 *   flow            LINK_ORDER_FLOWS — orderToShipments, or the ordersToShipment bulk
 *   detailFields    (unitType) => the summary card's rows
 *   open            whether the wizard is shown
 *   onClose         close handler
 *   onLinked        called once the save succeeded
 */
export const LinkShipmentsForOrder = ({
  module,
  orders = EMPTY_LIST,
  flow = LINK_ORDER_FLOWS.orderToShipments,
  detailFields,
  open,
  onClose,
  onLinked,
}) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null);
  const [linkOrderShipments, { isLoading: isSavingShipments }] = useLinkOrderShipmentsMutation();
  const [linkShipmentOrders, { isLoading: isSavingOrders }] = useLinkShipmentOrdersMutation();

  const isBulk = isBulkOrderFlow(flow);
  const first = orders[0];

  const queryArgs = useMemo(
    () => orders.map((row) => ({ orderId: row.id, search, sort })),
    [orders, search, sort],
  );

  const { candidates, loading } = useLinkableCandidates(
    linksApi.endpoints.getLinkableShipmentsForOrder,
    queryArgs,
    open,
  );

  const handleClose = useCallback(() => {
    setSearch('');
    setSort(null);
    onClose?.();
  }, [onClose]);

  // The mirror: bulk picked ONE shipment for the ticked orders, else one order takes N shipments.
  const handleSave = useCallback(
    async (entries, pickedShipmentId) => {
      const links = buildOrderLinksPayload(entries);
      const result = isBulk
        ? await linkShipmentOrders({ module, shipmentId: pickedShipmentId, orders: links })
        : await linkOrderShipments({ module, orderId: first?.id, shipments: links });
      if (result.error) return;
      onLinked?.();
      handleClose();
    },
    [isBulk, linkShipmentOrders, linkOrderShipments, module, first?.id, onLinked, handleClose],
  );

  // Closing unmounts the modal, so nothing it held survives into the next open.
  if (!open) return null;

  return (
    <LinkOrdersModal
      open={open}
      onClose={handleClose}
      title={t('LINK_SHIPMENTS_FOR_ORDER_TITLE', {
        orderNo: orders.map((row) => row.orderNo).join(', '),
      })}
      detailFields={detailFields}
      candidates={candidates}
      loading={loading}
      isSaving={isSavingShipments || isSavingOrders}
      flow={flow}
      bulkRows={isBulk ? orders : EMPTY_LIST}
      unassignedByUnit={isBulk ? undefined : first?.unassignedByUnit}
      onSearchChange={setSearch}
      onSortChange={setSort}
      onSave={handleSave}
    />
  );
};
